import { v4 as uuidv4 } from 'uuid';
import { Note, UUID, TypeOfNote, SmallNote, isAnnote, NoteDetails, Generator } from '@annote/types';

// the backend has a few fields the client doesn't need.
// this is the source of truth for the fields that are needed
const clientNoteFields = [
  'id',
  'userId',
  'type',
  'title',
  'value',
  'url',
  'createdFromId',
  'details',
  'createdAt',
  'syncedAt',
  'deletedAt',
  'links',
  'backlinks',
];

// make sure only the right fields for the backend are in the note
export const ensureOnlyNoteFields = (note: Note): Note => {
  return clientNoteFields.reduce<Partial<Note>>((acc, field) => {
    if (field in note) {
      // @ts-expect-error: Suppress type checking for dynamic key access
      acc[field as keyof Note] = note[field as keyof Note];
    }
    return acc;
  }, {} as Partial<Note>) as Note;
};

// Create a helper function to select notes without unecessary fields
export const selectNoteFields = clientNoteFields.join(', ');

export const createUUID = (): UUID => uuidv4();

export const createNote = (title: string, type: TypeOfNote, value?: string, createdFromId?: UUID): Note => {
  return {
    id: createUUID(),
    type,
    title,
    value: value || '',
    createdFromId,
    createdAt: new Date().toISOString(),
    backlinks: createdFromId ? [createdFromId] : [],
    links: [],
  } as Note;
};

export const smallNote = (note: Note): SmallNote => {
  return {
    id: note.id,
    title: note.title,
    type: note.type,
  };
};

// If its connected to an annote, we want to just skip through it and get what the annote is connected to
// Get all connected notes, skipping over annotes to get sources or takeaways the annote links to
export const getConnectedNotes = (
  startNote: Note,
  direction: 'backlinks' | 'links',
  getNote: (id: UUID) => Note | undefined, // Todo - this should maybe be getNotes
): Note[] => {
  const rawConnectionIds = direction === 'links' ? startNote.links : startNote.backlinks;
  const rawConnectedNotes = rawConnectionIds.map((id) => getNote(id)).filter((n) => n) as Note[];

  // For all annotes, go past them in direction and get all of their connections
  const annoteConnectionIds = rawConnectedNotes
    .filter((n) => isAnnote(n))
    .flatMap((a) => (direction === 'links' ? a.links : a.backlinks));

  // All ids that are connected to the start note (including connections through annotes)
  const ids = new Set([...rawConnectionIds, ...annoteConnectionIds].filter((id) => id !== startNote.id));

  //  now return all the notes that are not annotes
  return Array.from(ids)
    .map((id) => getNote(id))
    .filter((n) => n && !isAnnote(n)) as Note[];
};

// helper function used in a few tools
export const createSuggestedNote = (
  title: string,
  createdFromId: string | undefined,
  content?: string,
  toolName?: string,
  generator?: Generator,
): Note => {
  const newNote = createNote(title, 'takeaway', content, createdFromId || undefined);
  if (!newNote.details) {
    newNote.details = {} as NoteDetails;
  }
  newNote.details.generator = generator || 'ai';
  if (toolName) {
    newNote.details.tool = toolName;
  }
  // Note: suggestionId should be set by the caller after creating the database record
  if (createdFromId) {
    // TODO - should this
    newNote.links = [createdFromId];
  }
  return newNote;
};
